'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { decodeLocationParam } from '@/lib/location-handoff';
import { persistExplicitRegion } from '@/lib/region-selection';

/**
 * Consumes the `loc` query param that /region appends when sending the user to
 * a (possibly cross-origin) region site with a precisely chosen location.
 * Saves it as the UserLocation for this origin and strips the param from the
 * URL so it never lingers in shares/bookmarks. Renders nothing.
 *
 * Reads router state (not window.location) — during client-side navigation the
 * mount effect can fire before window.location reflects the new URL, so a
 * window read misses the param entirely.
 */
export function LocationHandoff() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    try {
      const raw = searchParams?.get('loc');
      if (raw === null || raw === undefined) return;
      const location = decodeLocationParam(raw);
      if (location) {
        // A `loc` param only ever comes from a selection the shopper made, so
        // it lands as a durable preference on THIS origin too — including the
        // server-readable cookie, which localStorage could never carry across
        // the domain hop. The decoded priority/source ride along untouched.
        persistExplicitRegion(location);
      }
      const remaining = new URLSearchParams(searchParams.toString());
      remaining.delete('loc');
      const query = remaining.toString();
      // Shallow URL swap — keep Next's history state intact, no server refetch.
      window.history.replaceState(window.history.state, '', query ? `${pathname}?${query}` : pathname);
    } catch {
      // Never let a malformed param break the page.
    }
  }, [pathname, searchParams]);

  return null;
}
